<template>
    <div class="signup-container">
        <div class="signup-wrapper">
            <div class="signup-card">
                <div class="signup-header">
                    <div class="logo-section">
                        <img :src="imageUrl" alt="Company Logo" class="company-logo" />
                        <div class="company-info" v-if="companySettingData.name">
                            <h3 class="company-name">{{ companySettingData.name }}</h3>
                            <div class="company-address">
                                <p v-if="companySettingData.address1">{{ companySettingData.address1 }}</p>
                                <p v-if="companySettingData.address2">{{ companySettingData.address2 }}</p>
                                <p v-if="companySettingData.city || companySettingData.state">
                                    <span v-if="companySettingData.city">{{ companySettingData.city }}</span>
                                    <span v-if="companySettingData.state">, {{ companySettingData.state }}</span>
                                </p>
                                <p v-if="companySettingData.country && companySettingData.country.country_name">
                                    {{ companySettingData.country.country_name }}
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="header-actions">
                        <div class="language-selector">
                            <button 
                                type="button"
                                class="language-button"
                                @click="showLanguageMenu = !showLanguageMenu"
                            >
                                <span :class="selectedLanguage.flagClass"></span>
                                <span>{{ selectedLanguage.label }}</span>
                                <i class="pi pi-chevron-down"></i>
                            </button>
                            <div v-if="showLanguageMenu" class="language-menu">
                                <button 
                                    v-for="language in languages" 
                                    :key="language.id"
                                    type="button"
                                    class="language-option"
                                    @click="changeLanguage(language.id)"
                                >
                                    <span :class="language.flagClass"></span>
                                    <span>{{ language.label }}</span>
                                </button>
                            </div>
                        </div>
                        
                        <div class="auth-link">
                            <span>{{ $translate('already_have_account') || 'Already have an account?' }}</span>
                            <router-link to="/login" class="link">
                                {{ $translate('sign_in') || 'Sign in' }}
                            </router-link>
                        </div>
                    </div>
                </div>
                
                <div class="signup-content">
                    <div v-if="currentStep !== 5" class="steps-navigation">
                        <div class="steps-container">
                            <div 
                                v-for="(step, index) in steps" 
                                :key="index"
                                class="step-item"
                                :class="{ 
                                    'step-active': currentStep === index + 1, 
                                    'step-completed': currentStep > index + 1 
                                }"
                            >
                                <div class="step-indicator">
                                    <div class="step-number" v-if="currentStep <= index + 1">
                                        {{ index + 1 }}
                                    </div>
                                    <i v-else class="pi pi-check step-check"></i>
                                </div>
                                <div class="step-content">
                                    <div class="step-title">{{ step.title }}</div>
                                    <div class="step-description">{{ step.description }}</div>
                                </div>
                                <div 
                                    v-if="index < steps.length - 1" 
                                    class="step-connector"
                                    :class="{ 'connector-completed': currentStep > index + 1 }"
                                ></div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="form-section">
                        <div v-if="currentStep === 1" class="form-step-content">
                            <div class="step-header">
                                <h3>{{ $translate('create_your_profile') || 'Create Your Profile' }}</h3>
                                <p>{{ $translate('shere_company_detail') || 'Share your company details to get started' }}</p>
                            </div>
                            <createProfile 
                                :themeColor="themeColor" 
                                :companySettingData="companySettingData" 
                                v-model="formData.customer"
                                @next-clicked="handleNextClicked" 
                            />
                        </div>
                        
                        <div v-else-if="currentStep === 2" class="form-step-content">
                            <div class="step-header">
                                <h3>{{ $translate('mailing_address') || 'Mailing Address' }}</h3>
                                <p>{{ $translate('company_main_address') || 'Enter your company main address' }}</p>
                            </div>
                            <mailAddress 
                                :themeColor="themeColor" 
                                v-model="formData.customer"
                                @next-clicked="handleNextClicked" 
                                @prev-clicked="handlePrevClicked" 
                            />
                        </div>
                        
                        <div v-else-if="currentStep === 3" class="form-step-content">
                            <div class="step-header">
                                <h3>{{ $translate('add_users') || 'Add Users' }}</h3>
                                <p>Add team members who will have access to the system</p>
                            </div>
                            <Contacts
                                :themeColor="themeColor"
                                :roles="roles"
                                :submitting="isSubmitting"
                                :formData="formData"
                                :has-additional-fields="hasAdditionalFields"
                                @submitForm="submitForm"
                                @next-clicked="handleNextClicked" 
                                @prev-clicked="handlePrevClicked"
                            />
                        </div>

                        <div v-else-if="currentStep === 4 && displayAdditionalInfo" class="form-step-content">
                            <div class="step-header">
                                <h3>{{ $translate('additional_info') || 'Additional Info' }}</h3>
                                <p>{{ $translate('additional_info_description') || 'Additional Info' }}</p>
                            </div>
                            <CustomerAdditionalInfo 
                                v-model="formData.customer"
                                :themeColor="themeColor"
                                :submitting="isSubmitting"
                                :formData="formData"
                                @submitForm="submitForm"
                                @prev-clicked="handlePrevClicked"
                            />
                        </div>
                        
                        <div v-else-if="currentStep === 5" class="form-step-content success-step">
                            <div class="success-content">
                                <div class="success-icon">
                                    <i class="pi pi-check-circle"></i>
                                </div>
                                <h2>{{ $translate('customer_on_borad_message') || 'Welcome! Your account has been created successfully.' }}</h2>
                                <p>Please check your email for activation instructions.</p>
                                <button 
                                    class="login-button" 
                                    @click="redirectToLogin()"
                                    type="button"
                                >
                                    {{ $translate('login') || 'Go to Login' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import createProfile from './createProfile'
import mailAddress from './mailAddress'
import Contacts from './Contacts'
import logoImage from '@/assets/images/logo.png'
import popupMixins from '@/mixins/popupMixins'
import CustomerAdditionalInfo from './CustomerAdditionalInfo.vue'

export default {
    components: {
        createProfile,
        mailAddress,
        Contacts,
        CustomerAdditionalInfo,
    },
    name: 'customerUsers',
    mixins: [popupMixins],
    data() {
        return {
            totalStep: 4,
            isSubmitting: false,
            currentStep: 1,
            hasAdditionalFields: false,
            showLanguageMenu: false,
            modelableId: 0,
            modelableType: "App\\Models\\Customer",
            steps: [
                {
                    title: this.$translate('create_your_profile') || 'Create Profile',
                    description: this.$translate('basic_information') || 'Basic information'
                },
                {
                    title: this.$translate('mailing_address') || 'Address Details', 
                    description: this.$translate('location_info') || 'Location information'
                },
                {
                    title: this.$translate('add_users') || 'Add Users',
                    description: this.$translate('team_members') || 'Team members'
                },
                {
                    title: this.$translate('additional_info') || 'Additional Info',
                    description: this.$translate('additional_info_description') || 'Additional Information'
                },
            ],
            formData: {
                customer: {
                    type_id: null,
                    customer_name: null,
                    first_name: null,
                    last_name: null,
                    email: null,
                    phone: null,
                    address1: null,
                    address2: null,
                    zip_code: null,
                    agreed_to_terms: false,
                    city: 'Kingston',
                    country_code: 'JM',
                    state_name: null,
                    customer_group_id: null,
                    customer_segment_id: null,
                    customer_agent_id: null,
                    // lifecycle_stage_id: null,
                    additional_fields: {},
                },
            },
            languages: [
                { id: 'en', label: 'English', flagClass: 'flag-uk' },
                { id: 'es', label: 'Español', flagClass: 'flag-spain' }
            ],
            selectedLanguage: { id: 'en', label: 'English', flagClass: 'flag-uk' },
            tableLoading: false
        };
    },
    async created() {
        this.$store.dispatch('company/fetch')
        await Promise.all([
            this.$store.dispatch('company/fetchConfiguration', 'customer_group_display_on_customer_onboard'),
            this.$store.dispatch('company/fetchConfiguration', 'customer_segment_display_on_customer_onboard'),
            this.$store.dispatch('company/fetchConfiguration', 'customer_agent_display_on_customer_onboard'),
        ])
    },
    async mounted() {
        document.addEventListener('click', this.handleClickOutside)
        const query = {
            modelableId: this.modelableId,
            modelableType: this.modelableType,
        }

        try {
            await this.$store.dispatch('additionalFieldNewMapping/fetchAll', {
                page: 0,
                query
            })
            this.hasAdditionalFields = true;
        } catch (err) {
            console.log(`[${this.componentKey}] fetch Error: `, err);
            this.doToast(this.$translate('error'), err.message, 'danger')
            this.hasAdditionalFields = false;
        } 
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    watch: {
        '$i18n.locale': {
            immediate: true,
            handler(val) {
                this.selectedLanguage = this.languages.find(language => language.id === val) || this.languages[0];
            }
        }
    },
    computed: {
        componentKey() {
            // Create a unique key for this component instance
            return `${this.modelableType}:${this.modelableId}`;
        },
        roles() {
            return this.$store.state.roles.items
        },
        themeColor() {
            const data = this.$store.state.company.settings || {}
            const colors = {
                'background-color': data.background_color + "!important",
                color: data.text_color + "!important"
            }
            return colors
        },
        customerSegmentDisplay() {
            const data = !!((this.$store.getters['company/configurations'] || []).find(x => x.key == 'customer_segment_display_on_customer_onboard')?.bool_value)
            return data
        },
        customerGroupDisplay() {
            console.log("this.$store.getters['company/configurations']", this.$store.getters['company/configurations'])
            const data = !!((this.$store.getters['company/configurations'] || []).find(x => x.key == 'customer_group_display_on_customer_onboard')?.bool_value)
            return data
        },
        customerAgentDisplay() {
            const data = !!((this.$store.getters['company/configurations'] || []).find(x => x.key == 'customer_agent_display_on_customer_onboard')?.bool_value)
            return data
        },
        displayAdditionalInfo() {
            return (this.hasAdditionalFields || 
                    this.customerGroupDisplay || 
                    this.customerSegmentDisplay || 
                    this.customerAgentDisplay) || false
        },
        companySettingData() {
            const data = this.$store.state.company.settings || {}
            return data
        },
        contacts() {
            return this.$store.state.customers.items
        },
        imageUrl() {
            let imageUrl
            const data = this.$store.state.company.settings || {}
            if (data && data.logo) {
                imageUrl = JSON.parse(data.logo).url
            } else {
                imageUrl = logoImage
            }
            return imageUrl
        },
    },
    methods: {
        handleClickOutside(event) {
            if (!event.target.closest('.language-selector')) {
                this.showLanguageMenu = false
            }
        },
        changeLanguage(langId) {
            if (this.$i18n.locale !== langId) {
                this.$i18n.locale = langId
                localStorage.setItem('language', langId)
            }
            this.showLanguageMenu = false
        },
        handleNextClicked() {
            if(this.formData.customer.agreed_to_terms){
                this.currentStep++;
            }else{
                this.doToast(
                    this.$translate('error'),
                    this.$translate('agreed_to_terms'),
                    'danger'
                )
            }
        },
        handlePrevClicked() {
            this.currentStep--;
        },
        redirectToLogin() {
            this.$router.push({ name: 'customerLogin' })
        },
        async submitForm() {
            try {
                if (this.formData.customer.type_id == 2 && this.contacts.length == 0)
                    throw new Error(this.$translate('one_user_required'))
                this.formData.contacts = this.contacts
                this.isSubmitting = true
                await this.$store.dispatch(`customers/createContact`, this.formData).then(() => {
                    this.currentStep = 4
                    this.$store.dispatch('customers/clear')
                    this.$nextTick(() => {
                        this.doToast(
                            this.$translate('created'),
                            this.$translate('activate_customer'),
                            'success'
                        )
                    })
                })
            } catch (error) {
                this.doToast(
                    this.$translate('error'),
                    error.message || error.response.data.error.message,
                    'danger'
                )
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
@import url('~primeicons/primeicons.css');

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.signup-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.signup-wrapper {
    width: 100%;
    max-width: 1400px;
}

.signup-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    min-height: 700px;
}

.signup-header {
    padding: 40px 60px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 30px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 20px;
}

.company-logo {
    width: 80px;
    height: 80px;
    object-fit: contain;
    border-radius: 12px;
}

.company-info {
    flex: 1;
}

.company-name {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}

.company-address {
    color: #64748b;
    font-size: 14px;
    line-height: 1.4;
}

.company-address p {
    margin: 2px 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 30px;
    flex-wrap: wrap;
}

.language-selector {
    position: relative;
}

.language-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    color: #475569;
    transition: all 0.2s;
}

.language-button:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
}

.language-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    z-index: 10;
    min-width: 150px;
    overflow: hidden;
}

.language-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #475569;
    text-align: left;
    transition: background 0.2s;
}

.language-option:hover {
    background: #f1f5f9;
}

.auth-link {
    font-size: 14px;
    color: #64748b;
    text-align: right;
}

.auth-link .link {
    color: #2F80ED;
    text-decoration: none;
    font-weight: 600;
    margin-left: 4px;
}

.auth-link .link:hover {
    color: #56CCF2;
}

.signup-content {
    padding: 40px 60px 60px;
}

.steps-navigation {
    margin-bottom: 50px;
    padding: 0 20px;
}

.steps-container {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    position: relative;
    max-width: 800px;
    margin: 0 auto;
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 1;
    z-index: 2;
}

.step-indicator {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #f1f5f9;
    border: 2px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    transition: all 0.3s ease;
    position: relative;
}

.step-item.step-active .step-indicator {
    background: linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%);
    border-color: #2F80ED;
    color: white;
    box-shadow: 0 4px 12px rgba(47, 128, 237, 0.3);
}

.step-item.step-completed .step-indicator {
    background: #10b981;
    border-color: #10b981;
    color: white;
}

.step-number {
    font-size: 16px;
    font-weight: 600;
    color: #64748b;
}

.step-item.step-active .step-number {
    color: white;
}

.step-check {
    font-size: 20px;
    color: white;
}

.step-content {
    text-align: center;
    max-width: 120px;
}

.step-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 4px;
    line-height: 1.2;
}

.step-item.step-active .step-title {
    color: #2F80ED;
}

.step-description {
    font-size: 12px;
    color: #64748b;
    line-height: 1.2;
}

.step-connector {
    position: absolute;
    top: 24px;
    left: 50%;
    right: -50%;
    height: 2px;
    background: #e2e8f0;
    z-index: 1;
    transition: all 0.3s ease;
}

.step-item:last-child .step-connector {
    display: none;
}

.step-connector.connector-completed {
    background: #10b981;
}

.form-step-content {
    max-width: 800px;
    margin: 0 auto;
}

.step-header {
    text-align: center;
    margin-bottom: 40px;
}

.step-header h3 {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}

.step-header p {
    font-size: 16px;
    color: #64748b;
}

.success-step {
    text-align: center;
    padding: 60px 20px;
}

.success-content {
    max-width: 500px;
    margin: 0 auto;
}

.success-icon {
    margin-bottom: 30px;
}

.success-icon i {
    font-size: 80px;
    color: #10b981;
}

.success-content h2 {
    font-size: 32px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 16px;
    line-height: 1.3;
}

.success-content p {
    font-size: 18px;
    color: #64748b;
    margin-bottom: 40px;
}

.login-button {
    background: linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 16px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
}

.login-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(86, 204, 242, 0.3);
}

/* Flag classes */
.flag-uk::before {
    content: '🇬🇧';
    margin-right: 4px;
}

.flag-spain::before {
    content: '🇪🇸';
    margin-right: 4px;
}

/* Responsive design */
@media (max-width: 992px) {
    .signup-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
    }
    
    .header-actions {
        width: 100%;
        justify-content: space-between;
    }
    
    .signup-content {
        padding: 30px 40px 50px;
    }
    
    .company-name {
        font-size: 20px;
    }
    
    .steps-container {
        flex-direction: column;
        align-items: stretch;
    }
    
    .step-item {
        flex-direction: row;
        align-items: center;
        margin-bottom: 20px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 12px;
    }
    
    .step-indicator {
        margin-bottom: 0;
        margin-right: 16px;
        width: 40px;
        height: 40px;
    }
    
    .step-content {
        text-align: left;
        max-width: none;
        flex: 1;
    }
    
    .step-connector {
        display: none;
    }
}

@media (max-width: 768px) {
    .signup-container {
        padding: 10px;
    }
    
    .signup-header {
        padding: 30px 30px;
    }
    
    .signup-content {
        padding: 20px 30px 40px;
    }
    
    .logo-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .company-logo {
        width: 60px;
        height: 60px;
    }
    
    .step-header h3 {
        font-size: 24px;
    }
    
    .success-content h2 {
        font-size: 26px;
    }
    
    .header-actions {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
}

@media (max-width: 576px) {
    .signup-header {
        padding: 20px 20px;
    }
    
    .signup-content {
        padding: 15px 20px 30px;
    }
    
    .step-header {
        margin-bottom: 30px;
    }
    
    .step-header h3 {
        font-size: 22px;
    }
    
    .success-icon i {
        font-size: 60px;
    }
    
    .success-content h2 {
        font-size: 24px;
    }
}
</style>